import React, { useState, useEffect } from 'react';
import { Button } from '../atoms/Button';
import { Select } from '../atoms/Select';
import { groupPayoutsByMonth, formatCurrency, formatDate } from '../../utils/date';
import { calculateTotalBNCAmount } from '../../utils/payoutCalculations';
import { needsValueFill } from '../../utils/fillMissingPayoutValues';
import type { Payout, PayoutType } from '../../types/payout';
import type { DeclarationFrequency } from '../../types/settings';

const TYPE_LABELS: Record<PayoutType, string> = {
  payout: 'Payout',
  achat: 'Achat',
  stacking: 'Stacking',
  vente: 'Vente',
  swap: 'Swap',
  transfert: 'Transfert',
};

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'Tous les types' },
  ...Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

interface PayoutHistoryProps {
  payouts: Payout[];
  declarationFrequency: DeclarationFrequency;
  bncRate?: number;
  onEditPayout?: (payout: Payout) => void;
  onDeletePayout?: (payoutId: string) => void;
  onFillMissingValues?: (payouts: Payout[]) => void;
  isFillingMissingValues?: boolean;
  setIsExportOpen: (value: boolean) => void;
}

export const PayoutHistory: React.FC<PayoutHistoryProps> = ({ payouts, bncRate = 0.34, onEditPayout, onDeletePayout, onFillMissingValues, isFillingMissingValues, setIsExportOpen }) => {
  const monthGroups = groupPayoutsByMonth(payouts);

  // Find the index of the current month, default to the last month if current month not found
  const getCurrentMonthIndex = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const index = monthGroups.findIndex(
      (group) => group.year === currentYear && group.month === currentMonth
    );

    // If current month not found, return the index of the most recent month
    return index !== -1 ? index : monthGroups.length - 1;
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState<PayoutType | 'all'>('all');

  // Update currentPage when monthGroups is populated (after payouts load)
  useEffect(() => {
    if (monthGroups.length > 0) {
      setCurrentPage(getCurrentMonthIndex());
    }
  }, [monthGroups.length]);
  const currentGroup = monthGroups[currentPage];
  const missingValuesCount = currentGroup?.payouts.filter(needsValueFill).length || 0;
  const filteredPayouts = currentGroup
    ? typeFilter === 'all'
      ? currentGroup.payouts
      : currentGroup.payouts.filter((p) => p.type === typeFilter)
    : [];
  const totalValue = payouts.reduce((sum, payout) => {
    if (payout.type === 'vente') {
      return sum - (payout.totalValue || 0);
    }
    if (payout.type !== 'transfert' && payout.type !== 'swap') {
      return sum + (payout.totalValue || 0);
    }
    return sum;
  }, 0);

  const monthNavigator = (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between">
        <Button
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            variant="secondary"
            className="flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Mois précédent
        </Button>

        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {currentGroup?.monthName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Page {currentPage + 1} sur {monthGroups.length}
          </p>
        </div>

        <Button
            onClick={() => setCurrentPage(prev => Math.min(monthGroups.length - 1, prev + 1))}
            disabled={currentPage === monthGroups.length - 1}
            variant="secondary"
            className="flex items-center"
        >
          Mois suivant
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );

  if (payouts.length === 0) {
    return (
      <div className="mt-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Aucun payout enregistré
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Commencez par déclarer votre premier payout ci-dessus
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-12 space-y-6">
      {/* Header avec statistiques de navigation */}
      <div className="bg-gradient-to-r from-slate-600 to-gray-700 dark:to-gray-900 rounded-xl shadow-lg text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <div>
              <h2 className="text-2xl font-bold mb-2">Historique des payouts</h2>
              <p className="text-slate-100">
                {payouts.length} transaction{payouts.length > 1 ? 's' : ''} • {monthGroups.length} mois
              </p>
            </div>
            <div className="max-w-6xl flex items-center">
              <button
                  onClick={() => setIsExportOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 h-[40px] bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-md transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Exporter les données
              </button>
            </div>

          </div>
          <div className="text-right">
            <div className={`text-3xl font-bold ${totalValue < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
              {formatCurrency(totalValue)}
            </div>
            <div className="text-slate-100 text-sm">
              Total général
            </div>
          </div>
        </div>
        
      </div>

      {/* Navigation par mois (avant le tableau) */}
      {monthGroups.length > 1 && monthNavigator}

      {/* Tableau du mois courant */}
      {currentGroup && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {currentGroup.monthName}
                </h3>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as PayoutType | 'all')}
                  options={TYPE_FILTER_OPTIONS}
                  className="w-auto"
                />
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold ${currentGroup.total < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {formatCurrency(currentGroup.total)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {currentGroup.payouts.length} transaction{currentGroup.payouts.length > 1 ? 's' : ''}
                </div>
                <div className="text-sm font-semibold text-purple-600 dark:text-purple-400 mt-1">
                  Total à déclarer au BNC : {formatCurrency(currentGroup.bncTotal)}
                  {' '}(URSSAF estimée : {formatCurrency(calculateTotalBNCAmount(currentGroup.payouts, bncRate))})
                </div>
                {onFillMissingValues && missingValuesCount > 0 && (
                  <Button
                    onClick={() => onFillMissingValues(currentGroup.payouts)}
                    disabled={isFillingMissingValues}
                    variant="secondary"
                    className="mt-2"
                  >
                    {isFillingMissingValues
                      ? 'Mise à jour en cours...'
                      : `Compléter ${missingValuesCount} valeur${missingValuesCount > 1 ? 's' : ''} manquante${missingValuesCount > 1 ? 's' : ''}`}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Crypto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Prix Unitaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Valeur Totale
                  </th>
                  {(onEditPayout || onDeletePayout) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredPayouts
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((payout) => (
                    <tr key={payout.id} className="hover:bg-gray-50 hover:dark:bg-gray-900">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payout.type === 'achat'
                            ? 'bg-green-100 text-green-800'
                            : payout.type === 'stacking'
                              ? 'bg-purple-100 text-purple-800'
                              : payout.type === 'vente'
                              ? 'bg-red-100 text-red-800'
                              : payout.type === 'swap'
                              ? 'bg-indigo-100 text-indigo-800'
                              : payout.type === 'transfert'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                        }`}>
                          {payout.type === 'achat'
                            ? 'Achat'
                            : payout.type === 'stacking'
                            ? 'Stacking'
                            : payout.type === 'vente'
                            ? 'Vente'
                            : payout.type === 'swap'
                            ? 'Swap'
                            : payout.type === 'transfert'
                            ? 'Transfert'
                            : 'Payout'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {formatDate(payout.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payout.type === 'swap' ? (
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {payout.sentCrypto?.symbol} → {payout.receivedCrypto?.symbol}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {payout.sentCrypto?.name} / {payout.receivedCrypto?.name}
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {payout.crypto?.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {payout.crypto?.symbol}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {payout.type === 'swap'
                          ? `${payout.sentAmount?.toFixed(8)} → ${payout.receivedAmount?.toFixed(8)}`
                          : payout.amount?.toFixed(8)
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {payout.type === 'swap'
                          ? `${formatCurrency(payout.sentUnitPrice || 0)} / ${formatCurrency(payout.receivedUnitPrice || 0)}`
                          : formatCurrency(payout.unitPrice || 0)
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-emerald-600">
                        {payout.type === 'swap'
                          ? `${formatCurrency(payout.sentTotalValue || 0)} / ${formatCurrency(payout.receivedTotalValue || 0)}`
                          : formatCurrency(payout.totalValue || 0)
                        }
                      </td>
                      {(onEditPayout || onDeletePayout) && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex items-center space-x-2">
                            {onEditPayout && (
                              <button
                                onClick={() => onEditPayout(payout)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                                title="Modifier ce payout"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                            {onDeletePayout && (
                              <button
                                onClick={() => {
                                  if (window.confirm('Êtes-vous sûr de vouloir supprimer ce payout ?')) {
                                    onDeletePayout(payout.id);
                                  }
                                }}
                                className="text-red-600 hover:text-red-900 transition-colors duration-200"
                                title="Supprimer ce payout"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Navigation par mois (après le tableau) */}
      {monthGroups.length > 1 && monthNavigator}
    </div>
  );
};